/** Admin QA view over the timeseries instrument catalogue (read-only). */

import type { HistoricalStatistics } from "./historical-statistics";

/** All timeseries data sources, used for the admin source filter. */
export const TIMESERIES_DATA_SOURCES = [
  "morningstar_funds",
  "morningstar_etf",
  "morningstar_category",
  "morningstar_index",
  "eodhd",
  "twelve",
  "custom",
  "combined",
  "currency",
] as const;

export type TimeseriesDataSource = (typeof TIMESERIES_DATA_SOURCES)[number];

/** A series whose latest data point is older than this many days reads as "stale". */
export const TIMESERIES_STALE_DAYS = 10;

/**
 * Status filter values for the admin QA view. Conditions overlap on a row (a row
 * can be both stale and missing currency); the filter shows every row matching
 * the chosen condition.
 * - `ok`          — has data, has currency, and a recent data point
 * - `no_data`     — meta row exists but no stored timeseries points
 * - `no_currency` — currency missing/empty
 * - `stale`       — has data but the latest point is older than TIMESERIES_STALE_DAYS
 * - `sync_disabled` — daily sync switched off (auto after repeated failures, or by admin)
 * - `fx_missing` — native currency has no USD FX pair; cannot convert to a report currency
 */
export const TIMESERIES_INSTRUMENT_STATUSES = [
  "ok",
  "no_data",
  "no_currency",
  "stale",
  "sync_disabled",
  "fx_missing",
] as const;

export type TimeseriesInstrumentStatusFilter = (typeof TIMESERIES_INSTRUMENT_STATUSES)[number];

/** One instrument row in the admin timeseries QA view, with data-coverage status. */
export interface TimeseriesInstrumentStatus {
  source: string;
  ticker: string;
  name: string | null;
  currency: string | null;
  isin: string | null;
  tenantId: string | null;
  /** Date the daily sync last refreshed this instrument's meta (YYYY-MM-DD). */
  lastUpdate: string | null;
  /** Number of timeseries data points stored. */
  pointCount: number;
  /** Earliest stored data point (YYYY-MM-DD), or null if no data. */
  firstDate: string | null;
  /** Latest stored data point (YYYY-MM-DD), or null if no data. */
  lastDate: string | null;
  /** Consecutive failed sync attempts (reset on success). */
  syncFailures: number;
  /** Set when sync is disabled (auto after repeated failures, or by admin). */
  syncDisabledAt: string | null;
  /** Last sync error message, if any. */
  syncError: string | null;
  /**
   * True when the instrument's native currency has no USD FX pair — every
   * cross-currency snapshot for it fails with "Missing FX rate", even though
   * its own sync can be healthy.
   */
  fxMissing: boolean;
}

export interface TimeseriesInstrumentListResponse {
  items: TimeseriesInstrumentStatus[];
  total: number;
  page: number;
  perPage: number;
  /** How many instruments (within the source/search scope) have no FX pair. */
  fxMissingTotal: number;
}

/** Response of GET /internal/admin/timeseries/:source/:ticker/data — raw stored points (QL-778). */
export interface TimeseriesInstrumentDataResponse {
  source: string;
  ticker: string;
  data: Array<{ date: string; value: number }>;
}

/**
 * Response of GET /internal/admin/timeseries/:source/:ticker/statistics (QL-706)
 * — calendar statistics of the stored series as it is: in the instrument's own
 * currency, with no patch and no conversion. A check of the raw data.
 */
export interface TimeseriesInstrumentStatisticsResponse {
  source: string;
  ticker: string;
  /** The instrument's own currency, from its meta; null when unknown. */
  currency: string | null;
  /** The stored last price and its date; null when none is captured. */
  lastPrice: { value: number; date: string | null } | null;
  statistics: HistoricalStatistics;
  /**
   * QL-736 — the date of the last stored point when it is more than 45 days old:
   * the statistics then end there instead of carrying it to today. Null when
   * they run to today.
   */
  measuredTo: string | null;
}

/** Request body for PATCH /internal/admin/timeseries/:source/:ticker/sync. */
export interface TimeseriesInstrumentSyncUpdateRequest {
  enabled: boolean;
}

export interface TimeseriesInstrumentSyncUpdateResponse {
  success: boolean;
  /**
   * Data points after the full resync that enabling sync triggers for
   * vendor-backed sources (0 = the refetch failed). Absent when disabling
   * or when the source has no vendor to refetch from (custom/combined).
   */
  points?: number;
}
